import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme, useTheme } from '@mui/material';
import MealAdvancedSearchCriteria from '../meal/meal_advanced_search_criteria';
import {
  GRC_RESULTS_GROUP_LABEL,
  useMealSearch,
} from '../common/meal_search_hook';
import PopupTitle from '../common/popup_title';
import { EntryType } from '../../API';
import { allIngredientNames } from '../../services/ingredients';
import RecipeCollectionsSearchTopResultsView from './recipe_collections_search_top_results_view';
import RecipeCollectionsSearchGroupResultsView from './recipe_collections_search_group_results_view';
import MealDetailModal from '../meal/meal_detail_modal';
import { userLocale } from '../../services/users';
import {
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
  useGetSharedProgrammeQuery,
  useGetFullMealQuery,
  useGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import { parentIDOfSharedMealID } from '../../services/smorg_board';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetSharedProgrammeRecipesBoardIDsForEndUserSearchQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
  SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
} from '../../services/end_user_published_resources';

const SEARCH_RESULTS_GROUP_LABEL = 'Search results';

const showNutritionMetrics = [
  'calories',
  'fat',
  'carbohydrate',
  'protein',
  'fibre',
];

const buildSearchCriteriaTheme = (appTheme) => {
  return createTheme(appTheme, {
    palette: {
      primary: {
        main: '#427a46',
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: 'var(--app-primary-font-color)',
            '&:before': {
              borderBottomColor: 'var(--app-primary-font-color) !important',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: 'var(--app-primary-font-color)',
            borderColor: 'var(--app-primary-font-color)',
            '&:hover': {
              color: 'var(--app-primary-font-color)',
            },
          },
        },
      },
    },
  });
};

const RecipeCollectionsSearchMealsView = () => {
  const navigate = useNavigate();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { sharedProgrammeID } = currentProgrammeEnrollment || {};

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    sharedProgrammeID,
    {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const { boardID: destinationBoardID, menuID: destinationMenuID } =
    useParams();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const appTheme = useTheme();

  const searchCriteriaTheme = buildSearchCriteriaTheme(appTheme);

  const [route, setRoute] = useState({ page: 'advancedCriteria' });

  const recentlyUsedSearchCriteria =
    userProfile?.recentlyUsedSearchCriteria || {};

  const [searchCriteria, setSearchCriteria] = useState(
    recentlyUsedSearchCriteria,
  );

  const locales = useMemo(
    () => sharedProgramme?.locales || [userLocale(userProfile)],
    [sharedProgramme?.locales, userProfile],
  );

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const myRecipesBoardIDs = useMemo(
    () =>
      (ownedRecipesBoards || []).map((board) => board.id).filter((id) => !!id),
    [ownedRecipesBoards],
  );

  const {
    data: programmeRecipesBoardsIDs,
    isLoading: isProgrammeRecipesBoardsIDsLoading,
  } = useGetSharedProgrammeRecipesBoardIDsForEndUserSearchQuery();

  const { data: spaceAssignedSharedRecipesBoardsIDs } =
    useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  // console.log(JSON.stringify({ spaceAssignedSharedRecipesBoardsIDs }));

  const groupsConfig = useMemo(
    () => [
      {
        groupLabel: SEARCH_RESULTS_GROUP_LABEL,
        parentIDs: [
          ...myRecipesBoardIDs,
          ...programmeRecipesBoardsIDs,
          ...spaceAssignedSharedRecipesBoardsIDs,
        ],
      },
    ],
    [
      myRecipesBoardIDs,
      programmeRecipesBoardsIDs,
      spaceAssignedSharedRecipesBoardsIDs,
    ],
  );

  const [results, estimatedCount, , onLoadMore, ingredientTreeNodes] =
    useMealSearch(searchCriteria, groupsConfig, 'recipes', locales, false);

  const loadedResultsCount = results
    ? results.reduce((acc, group) => acc + group.data.length, 0)
    : 0;

  const moreAvailable = results.some(
    (groupResults) => groupResults.moreAvailable,
  );

  const resultsWithActions = results.map((groupResults) => ({
    ...groupResults,
    entryType:
      groupResults.groupLabel === GRC_RESULTS_GROUP_LABEL
        ? EntryType.GRC_RECIPE
        : EntryType.MEAL,
  }));

  const onDismiss = () => navigate(-1);

  const { data: currentSpace } = useGetEndUserSpaceQuery();

  const allTags = (currentSpace?.publishedSearchTags || []).map((t) => t.tag);

  const onGoToGroup = useCallback((groupLabel) => {
    setRoute({ page: 'groupResults', groupLabel });
  }, []);

  const [showDetailForMealID, setShowDetailForMealID] = useState(null);

  const onClickResultDetail = (mealID) => {
    setShowDetailForMealID(mealID);
  };

  const onDismissDetail = () => {
    setShowDetailForMealID(null);
  };

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    programmeRecipesBoardsIDs,
    {
      skip:
        isProgrammeRecipesBoardsIDsLoading ||
        !programmeRecipesBoardsIDs ||
        programmeRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const parentIDInSharedMeals = parentIDOfSharedMealID(
    showDetailForMealID,
    sharedBoards,
  );

  const { data: distilledSharedMealsByParents } =
    useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
      skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
    });

  const visibleDistilledMealInSharedMeals = ((distilledSharedMealsByParents ||
    {})[parentIDInSharedMeals] || {})[showDetailForMealID];

  const alwaysShowNutrition = !isCommunityUser;

  const { data: fullOwnedMeal, isLoading: isFullOwnedMealLoading } =
    useGetFullMealQuery(showDetailForMealID, {
      skip: visibleDistilledMealInSharedMeals,
    });

  const { data: fullSharedMeal, isLoading: isFullSharedMealLoading } =
    useGetFullSharedMealQuery(showDetailForMealID, {
      skip: !visibleDistilledMealInSharedMeals,
    });

  const fullMeal = visibleDistilledMealInSharedMeals
    ? fullSharedMeal
    : fullOwnedMeal;
  const isFullMealLoading = visibleDistilledMealInSharedMeals
    ? isFullSharedMealLoading
    : isFullOwnedMealLoading;

  const isLoading = isFullMealLoading;

  if (showDetailForMealID && !isLoading && fullMeal) {
    return (
      <MealDetailModal
        fullMeal={fullMeal}
        isLoading={isLoading}
        menu={null}
        isReadOnly
        alwaysShowNutrition={alwaysShowNutrition}
        savingInProgress={false}
        showNutritionMetrics={showNutritionMetrics}
        derivedNutrition={fullMeal?.derivedNutrition}
        canScaleIngredients={false}
        scaleToServings={1}
        currentlyEditingInline={{ section: '', itemId: null }}
        ingredientSuggestions={[]}
        ingredientSuggestionsNetworkState={{ loading: false }}
        onInlineEditFocus={() => {}}
        onInlineEditChange={() => {}}
        onInlineEditBlur={() => {}}
        onAddArraySectionItem={() => {}}
        onRemoveArraySectionItem={() => {}}
        onIngredientCheckChange={() => {}}
        onEditScaleToServings={() => {}}
        onChangeScaleToServings={() => {}}
        resetScaleToServings={() => {}}
        onNewImageChosen={() => {}}
        onRuleChanged={() => {}}
        onAddonChange={() => {}}
        onTagsChange={() => {}}
        onNewImageUrlSet={() => {}}
        onDialogClose={onDismissDetail}
        onUpdateIngredientStructuredData={() => {}}
      />
    );
  }

  return (
    <div className="recipes-search">
      {route.page === 'advancedCriteria' && (
        <div className="recipes-search-form">
          <div style={{ color: 'var(--app-primary-font-color)' }}>
            <PopupTitle
              titleText="Search for a meal"
              backButtonEnabled
              onClickBack={onDismiss}
            />
          </div>
          <div style={{ height: 'calc(var(--app-height) - 106px)' }}>
            <ThemeProvider theme={searchCriteriaTheme}>
              <MealAdvancedSearchCriteria
                searchCriteria={searchCriteria}
                onChangeSearchCriteria={setSearchCriteria}
                totalResultsLoading={isLoading}
                totalResults={estimatedCount}
                loadedResultsCount={loadedResultsCount}
                moreAvailable={moreAvailable}
                onShowResults={() => {
                  setRoute({ page: 'topResults' });
                }}
                onDismiss={onDismiss}
                allIngredients={allIngredientNames(ingredientTreeNodes)}
                allTags={allTags}
              />
            </ThemeProvider>
          </div>
        </div>
      )}
      {route.page === 'topResults' && (
        <div className="recipes-search-results">
          <RecipeCollectionsSearchTopResultsView
            results={resultsWithActions}
            onGoToGroup={onGoToGroup}
            onLoadMore={onLoadMore}
            onBack={() => {
              setRoute({ page: 'advancedCriteria' });
            }}
            onDismiss={() => navigate(-1)}
            destinationBoardID={destinationBoardID}
            destinationMenuID={destinationMenuID}
            onClick={onClickResultDetail}
            searchCriteria={searchCriteria}
            onChangeSearchCriteria={setSearchCriteria}
          />
        </div>
      )}
      {route.page === 'groupResults' && (
        <div className="recipes-search-results">
          <RecipeCollectionsSearchGroupResultsView
            groupResults={resultsWithActions.find(
              (r) => r.groupLabel === route.groupLabel,
            )}
            onLoadMore={onLoadMore}
            onBack={() => {
              setRoute({ page: 'topResults' });
            }}
            onDismiss={() => navigate(-1)}
            destinationBoardID={destinationBoardID}
            destinationMenuID={destinationMenuID}
            onClick={onClickResultDetail}
            searchCriteria={searchCriteria}
            onChangeSearchCriteria={setSearchCriteria}
          />
        </div>
      )}
    </div>
  );
};

export default RecipeCollectionsSearchMealsView;
